import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BarChart3, Key, Home, Building2, ChevronRight, Check } from 'lucide-react';

const services = [
  {
    icon: BarChart3,
    title: 'Wertermittlung',
    description: 'Eine fundierte Wertermittlung ist die Basis jeder erfolgreichen Immobilientransaktion. Mit aktuellen Marktdaten und langjähriger Erfahrung ermittle ich den realistischen Wert Ihrer Immobilie.',
    benefits: [
      'Kostenlose Ersteinschätzung',
      'Marktgerechte Bewertung',
      'Fundierte Analysemethoden',
      'Detaillierter Bewertungsbericht'
    ],
    href: '/leistungen/wertermittlung'
  },
  {
    icon: Key,
    title: 'Immobilienverkauf',
    description: 'Der Verkauf einer Immobilie erfordert Expertise, Diskretion und ein starkes Netzwerk. Ich begleite Sie von der Bewertung bis zur Schlüsselübergabe – professionell und persönlich.',
    benefits: [
      'Diskrete Vermarktung',
      'Professionelle Exposés',
      'Qualifizierte Käuferauswahl',
      'Verhandlungsführung'
    ],
    href: '/leistungen/verkauf'
  },
  {
    icon: Home,
    title: 'Vermietung',
    description: 'Die richtige Mieterauswahl ist entscheidend für eine langfristig erfolgreiche Vermietung. Ich finde den passenden Mieter für Ihre Immobilie und kümmere mich um alle Details.',
    benefits: [
      'Bonitätsprüfung',
      'Mietvertragsgestaltung',
      'Übergabeprotokoll',
      'Nachbetreuung'
    ],
    href: '/leistungen/vermietung'
  },
  {
    icon: Building2,
    title: 'Vermittlung',
    description: 'Ob Wohnungen, Liegenschaften oder Gewerbeobjekte – ich vermittle Immobilien aller Art. Mit meinem Netzwerk und meiner Marktkenntnis finde ich das passende Objekt für Sie.',
    benefits: [
      'Wohnungsvermittlung',
      'Liegenschaftsvermittlung',
      'Gewerbevermittlung',
      'Individuelle Suche'
    ],
    href: '/leistungen/vermittlung'
  }
];

export default function Leistungen() {
  return (
    <div data-testid="leistungen-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <span className="text-[#d1af6a]">Leistungen</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
              UNSERE LEISTUNGEN
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
              Kompetenz in allen Bereichen
            </h1>
            <p className="text-[#c8c6c4] text-lg leading-relaxed">
              Von der Wertermittlung über den Verkauf bis zur Vermietung – 
              ich biete Ihnen ein umfassendes Leistungsspektrum rund um Ihre Immobilie.
              Persönlich, professionell und mit höchstem Anspruch an Qualität.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="card-luxury p-8 lg:p-10">
                    <div className="w-16 h-16 bg-[#d1af6a]/10 flex items-center justify-center mb-6">
                      <service.icon className="w-8 h-8 text-[#d1af6a]" />
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl text-white mb-4">
                      {service.title}
                    </h2>
                    <p className="text-[#c8c6c4] leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {service.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-center gap-3 text-white text-sm">
                          <Check className="w-4 h-4 text-[#d1af6a] flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={service.href}
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid={`service-link-${service.title.toLowerCase()}`}
                    >
                      Mehr erfahren
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="aspect-[4/3] bg-[#1a2640] border border-[#d1af6a]/20 flex items-center justify-center">
                    <service.icon className="w-32 h-32 text-[#d1af6a]/20" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a2640]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-4">
            Bereit für eine persönliche Beratung?
          </h2>
          <p className="text-[#c8c6c4] mb-8">
            Kontaktieren Sie mich für ein unverbindliches Erstgespräch.
          </p>
          <Link to="/kontakt" className="btn-primary">
            Kontakt aufnehmen
          </Link>
        </div>
      </section>
    </div>
  );
}
